/**
 * Public storefront API client (TNK-51). These endpoints are unauthenticated
 * and same-origin, so plain fetch is used (no bearer header, no auth side
 * effects). All return shapes mirror the public projection in
 * apps/api/src/routes/store.ts — deliberately free of seller-only fields.
 */

export interface StorefrontProfile {
  storeName: string;
  logoRef: string | null;
  watermarkRef: string | null;
  heroRef: string | null;
  /** Hero band tuning (TNK-204); null = app defaults (280px / 50% / cover). */
  heroHeight: number | null;
  heroOpacity: number | null;
  /** CSS object-fit: "cover" | "contain" | "fill". */
  heroFit: string | null;
  /** /shop landing title block position (TNK-205); null = center / top. */
  landingTitleAlign: string | null;
  landingTitleValign: string | null;
  /** Show the configurable hero band on the /shop landing (TNK-206). */
  landingShowHero: boolean | null;
  /** Storefront ticker config (TNK-208); null = default (shipped on, fade). */
  tickerConfig: TickerConfig | null;
}

/** Storefront ticker config (TNK-208) — mirrors the server's TickerConfig. */
export interface TickerConfig {
  shipped?: boolean;
  available?: boolean;
  pricesFrom?: boolean;
  justSold?: boolean;
  recentlyAdded?: boolean;
  games?: boolean;
  custom?: string[];
  motion?: "fade" | "marquee" | "static";
  speed?: "slow" | "medium" | "fast";
  /** Space between items, px (also the minimum when items auto-spread). */
  gap?: number;
}

/**
 * Storefront eligibility gate (TNK-212) — mirrors the server's StorefrontGate.
 * A single appears only if it matches AT LEAST ONE group; within a group every
 * condition must hold (OR of AND-groups). Empty/null = no gate (everything
 * eligible shows). Each condition names a card attribute (see the editor's
 * attribute catalog) with an allow-list (`values`) or a numeric range.
 */
export interface StorefrontGateCondition {
  attr: string;
  values?: string[];
  min?: number;
  max?: number;
}
export interface StorefrontGateGroup {
  conditions: StorefrontGateCondition[];
}
export interface StorefrontGate {
  groups: StorefrontGateGroup[];
}

export interface StorefrontCard {
  id: string;
  name: string;
  game: string;
  setName: string;
  setCode: string;
  cardNumber: string;
  rarity: string;
  variant: string;
  condition: string;
  finish: string | null;
  marketPrice: number;
  expectedSalePrice: number;
  imageFrontRef: string | null;
  imageBackRef: string | null;
  // Print + condition metadata (TNK-115 phase B1). All nullable — older
  // imports + non-Pokemon games won't carry these.
  year: string | null;
  language: string | null;
  graded: boolean | null;
  gradingCompany: string | null;
  tcgSupertype: string | null;
  tcgSubtypes: string[] | null;
  tcgTypes: string[] | null;
  tcgHp: string | null;
  tcgPokedexNumbers: number[] | null;
  tcgArtist: string | null;
  tcgSeries: string | null;
  // MTG-native printing data (TNK-121). Pokemon + other games leave null.
  mtgManaCost: string | null;
  mtgPowerToughness: string | null;
  mtgLoyalty: string | null;
  mtgOracleText: string | null;
  mtgColors: string[] | null;
  // Live eBay listing surfacing (TNK-119). `listingType` is null when
  // there's no active listing; "Auction" → buyer should bid on eBay
  // instead of reserving locally; "FixedPrice" → local reservation
  // path stays available alongside the eBay link.
  ebayListingId: string | null;
  ebayListingUrl: string | null;
  listingType: string | null;
}

/** null = no storefront published / feature off (404). Throws on other errors. */
export async function fetchStorefrontProfile(): Promise<StorefrontProfile | null> {
  const res = await fetch("/api/store/profile");
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Storefront unavailable (${res.status})`);
  return (await res.json()) as StorefrontProfile;
}

export async function fetchStorefrontInventory(): Promise<StorefrontCard[]> {
  const res = await fetch("/api/store/inventory");
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`Storefront unavailable (${res.status})`);
  const data = (await res.json()) as { cards: StorefrontCard[] };
  return data.cards;
}

/**
 * TNK-124 phase G — single active card lookup. Backs the cold-start
 * path on `/shop/card/$id` and the cart drawer when the card isn't in
 * the warmed first page. Returns null on 404 so callers can fall back
 * to the sold-archive lookup or the unavailable state. The endpoint
 * uses the same public eligibility predicate as `/store/inventory`.
 */
export async function fetchStorefrontCard(
  id: string,
): Promise<StorefrontCard | null> {
  const res = await fetch(`/api/store/cards/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Card lookup failed (${res.status})`);
  return (await res.json()) as StorefrontCard;
}

/**
 * Public locked-bundle listing (TNK-212 phase 2a) — a curated `product` the
 * operator opted into the website store. `coverRef` is a single image ref
 * (resolve through /api/images/:id like any card image): the operator's custom
 * cover photo if set, else one member card's front image; null when neither
 * exists. Mirrors `/api/store/bundles`.
 */
export interface StorefrontBundle {
  id: string;
  name: string;
  description: string;
  productType: string;
  price: number;
  cardCount: number;
  coverRef: string | null;
}

/** `/api/store/bundles/:id` — a bundle plus the public projection of its
 *  member cards (rendered as normal card tiles on the detail page). */
export interface StorefrontBundleDetail {
  bundle: StorefrontBundle;
  cards: StorefrontCard[];
}

/** Listed bundles for the active store. Empty on 404 (no storefront). */
export async function fetchStorefrontBundles(): Promise<StorefrontBundle[]> {
  const res = await fetch("/api/store/bundles");
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`Storefront unavailable (${res.status})`);
  const data = (await res.json()) as { bundles: StorefrontBundle[] };
  return data.bundles;
}

/** Single bundle detail; null on 404 (not listed / not available). */
export async function fetchStorefrontBundle(
  id: string,
): Promise<StorefrontBundleDetail | null> {
  const res = await fetch(`/api/store/bundles/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Bundle lookup failed (${res.status})`);
  return (await res.json()) as StorefrontBundleDetail;
}

/** Public sold-archive entry (TNK-117). One row per shipped Card sale —
 *  Product bundles deliberately excluded in v1 because the contained
 *  card rows are removed on bundle ship. */
export interface SoldCard {
  id: string;
  name: string;
  game: string;
  setName: string;
  setCode: string;
  cardNumber: string;
  rarity: string;
  variant: string;
  condition: string;
  finish: string | null;
  imageFrontRef: string | null;
  imageBackRef: string | null;
  tcgSupertype: string | null;
  tcgArtist: string | null;
  soldAt: string;
}

export interface SoldFeedPage {
  cards: SoldCard[];
  total: number;
  limit: number;
  offset: number;
}

/** Single-card lookup against the sold archive — backs the search-
 *  continuity fallback on /shop/card/$id when the card isn't in the
 *  active feed. Returns null on 404 (no such sold card). */
export async function fetchStorefrontSoldCard(
  id: string,
): Promise<SoldCard | null> {
  const res = await fetch(`/api/store/sold/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Sold lookup failed (${res.status})`);
  return (await res.json()) as SoldCard;
}

export async function fetchStorefrontSold(
  opts: { limit?: number; offset?: number } = {},
): Promise<SoldFeedPage> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(`/api/store/sold${qs}`);
  if (res.status === 404) return { cards: [], total: 0, limit: 0, offset: 0 };
  if (!res.ok) throw new Error(`Sold archive unavailable (${res.status})`);
  return (await res.json()) as SoldFeedPage;
}

/** Paginated feed page. `nextCursor === null` signals the end of the
 *  feed — no further `loadMore` call needs to be made (TNK-124 phase D).
 *  `total` reflects the count of rows matching the current filter set
 *  (not just the loaded page), so the hero can honestly read "60 of N
 *  matching" rather than echo back the loaded slice. */
export interface StorefrontFeedPage {
  cards: StorefrontCard[];
  nextCursor: string | null;
  total: number;
}

/** Sort modes accepted by the storefront feed. Mirrors `SortBy` in
 *  shopFilters.ts and the api's `SortKey` (TNK-124 phase E). */
export type StorefrontSort =
  | "newest"
  | "price-asc"
  | "price-desc"
  | "name-asc"
  | "rarity";

/** Server-side filter axes that drive `/api/store/inventory` (TNK-124
 *  phase E). All multi-value axes are sent as comma-separated lists;
 *  empty arrays + zero / undefined bounds are omitted. */
export interface StorefrontFeedFilters {
  games: string[];
  rarities: string[];
  types: string[];
  elements: string[];
  colors: string[];
  conditions: string[];
  priceMin: number;
  priceMax: number;
  foilOnly: boolean;
}

export interface StorefrontFeedParams {
  limit?: number;
  cursor?: string | null;
  q?: string | null;
  sort?: StorefrontSort;
  filters?: StorefrontFeedFilters;
}

/** Fetches one page of the cursor-paginated storefront inventory feed.
 *  `q` runs server-side fuzzy search (pg_trgm), `sort` drives the
 *  ORDER BY, and the entries on `filters` route to the matching server
 *  filter axis. `cursor` continues the previous page; `null` /
 *  `undefined` fetches the first page. */
export async function fetchStorefrontFeed(
  params: StorefrontFeedParams = {},
): Promise<StorefrontFeedPage> {
  const search = new URLSearchParams();
  search.set("limit", String(params.limit ?? 60));
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.q && params.q.trim()) search.set("q", params.q.trim());
  if (params.sort && params.sort !== "newest") search.set("sort", params.sort);
  const f = params.filters;
  if (f) {
    if (f.games.length) search.set("game", f.games.join(","));
    if (f.rarities.length) search.set("rarity", f.rarities.join(","));
    if (f.types.length) search.set("type", f.types.join(","));
    if (f.elements.length) search.set("element", f.elements.join(","));
    if (f.colors.length) search.set("color", f.colors.join(","));
    if (f.conditions.length) search.set("condition", f.conditions.join(","));
    if (f.priceMin > 0) search.set("priceMin", String(f.priceMin));
    if (f.priceMax > 0) search.set("priceMax", String(f.priceMax));
    if (f.foilOnly) search.set("foil", "1");
  }
  const res = await fetch(`/api/store/inventory?${search}`);
  if (res.status === 404) return { cards: [], nextCursor: null, total: 0 };
  if (!res.ok) throw new Error(`Storefront unavailable (${res.status})`);
  return (await res.json()) as StorefrontFeedPage;
}

/** Server-aggregated filter options the buyer's sidebar consumes
 *  (TNK-124 phase C). Mirrors the client-side `FilterOptions` shape so
 *  the phase E ShopFilters swap is a straight drop-in.
 *
 *  `priceMin`/`priceMax` reflect the FULL eligible inventory (not the
 *  loaded subset) so the hero's price-range badge tells the buyer what
 *  the store actually carries — see TNK-124 phase G follow-up. `total`
 *  is the row count of eligible inventory for the "of N" denominator. */
export interface StorefrontFacetsResponse {
  games: string[];
  rarities: string[];
  /** Cross-game tcg_supertype union. Kept for legacy callers; new code
   *  prefers `pokemonEnergies` + `magicCardTypes` for clean per-game
   *  filter sections. */
  types: string[];
  /** Legacy combined element axis (Pokémon energies + Magic card
   *  types). Kept for back-compat; new code uses the split fields. */
  elements: string[];
  /** Pokémon-only energies (Fire / Water / Psychic / …). Sourced from
   *  the `tcg_types` jsonb array filtered to the canonical
   *  POKEMON_ENERGIES vocabulary so junk values stay out of the pill
   *  row. */
  pokemonEnergies: string[];
  /** Magic-only primary card types (Creature / Sorcery / Land / …).
   *  Sourced from `tcg_supertype` filtered to the canonical
   *  MAGIC_CARD_TYPES vocabulary. */
  magicCardTypes: string[];
  /** WUBRG colors + `C` (Colorless) when at least one Magic card has
   *  an explicitly-empty `mtg_colors` array. */
  colors: string[];
  conditions: string[];
  priceMin: number;
  priceMax: number;
  priceCap: number;
  total: number;
}

export async function fetchStorefrontFacets(): Promise<StorefrontFacetsResponse | null> {
  const res = await fetch("/api/store/facets");
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Facets unavailable (${res.status})`);
  return (await res.json()) as StorefrontFacetsResponse;
}

/** Display price: the seller's ask if set, else the market price. */
export function storefrontPrice(card: StorefrontCard): number {
  return card.expectedSalePrice > 0 ? card.expectedSalePrice : card.marketPrice;
}

export type PickupPreference = "pickup" | "ship" | "either";

export interface ReservationInput {
  cardIds: string[];
  /** TNK-212 phase 2b — locked-bundle product ids reserved alongside cards. */
  productIds?: string[];
  /** TNK-167 phase B+ — server ignores this and uses buyersTable.email from
   *  the authenticated session. Kept on the type for callers that still
   *  populate it; they don't break, the value just isn't used. */
  buyerEmail?: string;
  buyerName?: string;
  buyerNote?: string;
  pickupPreference?: PickupPreference;
  shippingAddress?: Record<string, string>;
}

/** TNK-127 phase 1 — reservation item shape served by both the buyer
 *  status page (`/api/store/reservations/:token`) and the seller queue
 *  (`/api/reservations`). Identity fields are snapshots captured at
 *  submit time; `current*` fields and `stillAvailable` are live
 *  enrichment that may diverge from the snapshot if the seller has
 *  since edited / archived / sold the card. TNK-163 added per-item
 *  status + live rarity/game/MTG colour data so the UI can dim
 *  removed items + tint the thumb borders. */
export interface ReservationItemView {
  /** TNK-212 phase 2b — 'card' (default) or 'bundle'. Bundle items carry
   *  `productId` + `cardCount` and skip the card-only fields. */
  kind?: "card" | "bundle";
  /** Bundle product id (kind='bundle'); null/absent for cards. */
  productId?: string | null;
  /** Number of cards in the bundle (kind='bundle'). */
  cardCount?: number | null;
  /** Original card id, or the snapshot id when the card has been hard-deleted. */
  id: string;
  /** Internal `reservation_items` row id — the buyer payload omits this. */
  itemId?: string;
  /** Per-item lifecycle: requested | held | released | sold |
   *  removed_unavailable | substituted. Seller queue + buyer page both
   *  use this to render dimmed/strike-through for the operator-removed
   *  items vs. still-active ones. */
  itemStatus?: string;
  name: string;
  setName: string | null;
  cardNumber: string | null;
  condition: string | null;
  imageFrontRef: string | null;
  /** Price the buyer agreed to at submit time. */
  requestedPrice: number | null;
  /** Live market price now; null when the live card no longer exists. */
  currentMarketPrice: number | null;
  /** Live listing status now; null when the live card no longer exists. */
  currentListingStatus: string | null;
  /** True when the card row still exists and the seller could fulfill it. */
  stillAvailable: boolean;
  /** Live card attributes used for thumbnail border/glow tinting in the
   *  seller queue (TNK-163). Null when the live card is gone. */
  game?: string | null;
  rarity?: string | null;
  mtgColors?: string[] | null;
  tcgSupertype?: string | null;
}

/** TNK-127 — a reservation item is "no longer available" when its card is gone
 *  (row deleted/archived), sold, packaged into a bundle, or hard-held by
 *  ANOTHER reservation's offer. `held` means THIS reservation holds it, so that
 *  stays available to this buyer. Used to flag stale items on the buyer + seller
 *  views when a contended card is sold/bundled elsewhere — selling never mutates
 *  other reservations, so this is the read-side signal. */
export function isReservationItemUnavailable(
  item: ReservationItemView,
): boolean {
  if (!item.stillAvailable) return true; // card row deleted/archived
  const s = item.currentListingStatus;
  if (s === "Sold" || s === "In Package") return true;
  if (s === "Reserved" && item.itemStatus !== "held") return true;
  return false;
}

/** TNK-127 phase 2 — formal priced offer attached to a reservation. */
export interface ReservationOfferView {
  id: string;
  reservationId: string;
  status: string;
  createdByType: string;
  subtotal: number;
  discount: number;
  shipping: number;
  total: number;
  message: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  declinedAt: string | null;
  createdAt: string;
}

export interface ReservationStatus {
  status: string;
  requestedAt: string;
  expiresAt: string;
  buyerName: string | null;
  sellerNote: string | null;
  /** TNK-127 phase 3 — manual payment link the seller pasted; rendered as a
   *  "Pay now" button while the reservation is AwaitingPayment. */
  paymentLinkUrl: string | null;
  /** TNK-127 phase 4 — buyer counter-offers left on this reservation (cap). */
  counterAttemptsRemaining: number;
  cards: ReservationItemView[];
  /** TNK-127 phase 2 — latest offer attached to the reservation. The
   *  buyer page renders Accept/Decline against this when status is
   *  `sent` (and `expiresAt` hasn't passed). */
  currentOffer: ReservationOfferView | null;
}

/** Creates a reservation; resolves to the buyer's status token. Throws with a
 *  user-facing message on validation / availability errors. */
export async function createReservation(
  input: ReservationInput,
): Promise<{ id: string; statusToken: string; reservedCount: number }> {
  // TNK-167 phase C — buyerFetch attaches X-CSRF-Token from the buyer
  // cookie pair. The endpoint is now requireBuyer-gated server-side, so
  // an unauthed POST 401s with { requireAuth: true } and the caller
  // (shop.reserve.tsx) bounces to /shop/account/login.
  const { buyerFetch } = await import("@/lib/buyerClient");
  const res = await buyerFetch("/api/store/reservations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      requireAuth?: boolean;
    };
    if (res.status === 401 && body.requireAuth) {
      throw new Error("Sign in required.");
    }
    throw new Error(body.error ?? `Could not reserve (${res.status})`);
  }
  return (await res.json()) as {
    id: string;
    statusToken: string;
    reservedCount: number;
  };
}

/** null = no reservation with that token (404). */
export async function fetchReservation(
  token: string,
): Promise<ReservationStatus | null> {
  const res = await fetch(
    `/api/store/reservations/${encodeURIComponent(token)}`,
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Reservation unavailable (${res.status})`);
  return (await res.json()) as ReservationStatus;
}

/** TNK-127 phase 2 — buyer accepts an outstanding offer. Server
 *  refuses 409 if the offer is no longer `sent`, 410 if expired.
 *  Returns the updated offer + reservation status so the page can
 *  reflect it without a refetch. */
export async function acceptReservationOffer(
  token: string,
  offerId: string,
): Promise<{ offer: ReservationOfferView; reservationStatus: string }> {
  const res = await fetch(
    `/api/store/reservations/${encodeURIComponent(token)}/offers/${encodeURIComponent(offerId)}/accept`,
    { method: "POST" },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Accept failed (${res.status})`);
  }
  return (await res.json()) as {
    offer: ReservationOfferView;
    reservationStatus: string;
  };
}

export async function declineReservationOffer(
  token: string,
  offerId: string,
): Promise<{ offer: ReservationOfferView; reservationStatus: string }> {
  const res = await fetch(
    `/api/store/reservations/${encodeURIComponent(token)}/offers/${encodeURIComponent(offerId)}/decline`,
    { method: "POST" },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Decline failed (${res.status})`);
  }
  return (await res.json()) as {
    offer: ReservationOfferView;
    reservationStatus: string;
  };
}

/** TNK-127 phase 4 — buyer counters the seller's offer with a proposed total.
 *  Capped server-side (429 when out of attempts). Returns the new offer +
 *  reservation status + remaining counters. */
export async function counterReservationOffer(
  token: string,
  offerId: string,
  total: number,
): Promise<{
  offer: ReservationOfferView;
  reservationStatus: string;
  countersRemaining: number;
}> {
  const res = await fetch(
    `/api/store/reservations/${encodeURIComponent(token)}/offers/${encodeURIComponent(offerId)}/counter`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ total }),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Counter failed (${res.status})`);
  }
  return (await res.json()) as {
    offer: ReservationOfferView;
    reservationStatus: string;
    countersRemaining: number;
  };
}
